import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Wallet as WalletIcon, Loader2, MessageCircle } from "lucide-react";
import { useAccount, photoUrl, errMsg } from "@/lib/account";
import { AiBadge } from "@/components/ProfileCard";
import { usePrefs } from "@/lib/prefs";

const Avatar = ({ p, size = "w-11 h-11" }) => (
  <div className={`${size} rounded-full overflow-hidden bg-[#0D0F15] border border-amber-500/30 shrink-0 flex items-center justify-center`}>
    {p?.photos?.[0] ? <img src={photoUrl(p.photos[0])} alt="" className="w-full h-full object-cover object-top" /> : <span className="font-display text-amber-300">{p?.name?.[0]}</span>}
  </div>
);

const Inbox = ({ items, active }) => (
  <aside className={`lg:col-span-4 card-royal rounded-2xl overflow-hidden flex flex-col ${active ? "hidden lg:flex" : "flex"}`} data-testid="chat-inbox">
    <div className="p-5 border-b border-slate-800 font-display text-2xl">Messages</div>
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      {items.length === 0 && <p className="p-6 text-sm text-slate-500">No conversations yet. <Link to="/browse" className="text-amber-300 underline">Find someone</Link></p>}
      {items.map((c) => (
        <Link key={c.conversation_id} to={`/chat/${c.other.id}`} data-testid={`inbox-item-${c.other.id}`}
          className={`flex gap-3 p-4 items-center border-b border-slate-800/60 hover:bg-amber-500/5 transition-colors ${active === c.other.id ? "bg-amber-500/10" : ""}`}>
          <Avatar p={c.other} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2"><span className="font-medium truncate">{c.other.name}</span>{c.other.is_ai && <span className="text-[10px] text-purple-300">AI</span>}</div>
            <p className="text-xs text-slate-400 truncate">{c.last_message}</p>
          </div>
        </Link>
      ))}
    </div>
  </aside>
);

const Thread = ({ pid, onSent }) => {
  const { authed, setAccount, account } = useAccount();
  const { t } = usePrefs();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const end = useRef();

  const load = useCallback(() => authed("get", `/conversations/with/${pid}`).then((d) => { setData(d); setAccount(d.account); }).catch((e) => setError(errMsg(e))), [pid, authed, setAccount]);
  useEffect(() => { setData(null); setError(null); load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const el = end.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [data?.messages?.length, sending]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const msg = text; setText(""); setSending(true);
    try {
      const r = await authed("post", `/conversations/with/${pid}/messages`, { text: msg });
      setAccount(r.account);
      setData((d) => ({ ...d, messages: [...d.messages, r.message, ...(r.reply ? [r.reply] : [])] }));
      onSent();
    } catch (err) { setText(msg); toast.error(errMsg(err)); }
    finally { setSending(false); }
  };

  if (error) return <div className="lg:col-span-8 card-royal rounded-2xl p-10 text-center" data-testid="chat-error"><p className="text-slate-300">{error}</p>
    <Link to="/me" className="btn-gold inline-block mt-6 px-6 py-2.5 rounded-full">Create profile</Link></div>;
  if (!data) return <div className="lg:col-span-8 card-royal rounded-2xl flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  const { other, me, messages } = data;
  const cost = data.message_cost ?? 1;
  const blocked = data.needs_interest || !account?.plan_active || (account?.balance ?? 0) < cost;

  return (
    <section className="lg:col-span-8 card-royal rounded-2xl flex flex-col overflow-hidden" data-testid="chat-thread">
      <div className="p-4 border-b border-slate-800 flex items-center gap-3">
        <Link to={`/profile/${other.id}`}><Avatar p={other} /></Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-display text-xl" data-testid="chat-other-name">{other.name}</span>{other.is_ai && <AiBadge testId="chat-ai-badge" />}</div>
          <p className="text-xs text-slate-400">{other.profession} · {other.city}</p>
        </div>
        <Link to="/wallet" data-testid="chat-cost-badge" className="hidden sm:flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
          <WalletIcon className="w-3 h-3" /> ₹{account?.balance ?? 0} · ₹{cost}/msg{cost > 1 && " · cross-border"}
        </Link>
      </div>
      <div ref={end} className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin mandala-bg" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-center text-sm text-slate-500 py-10" data-testid="chat-first-cost">Say hello to {other.name.split(" ")[0]} — each message costs ₹{cost}{cost > 1 ? " (cross-border chat)" : ""}.</p>}
        {messages.map((m) => {
          const mine = m.sender_profile_id === me.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={mine ? "chat-msg-mine" : "chat-msg-other"}>
              <div className={`max-w-[78%] px-4 py-3 text-sm leading-relaxed shadow-md ${mine ? "bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-2xl rounded-tr-none" : "bg-slate-800/90 text-slate-100 border border-slate-700/60 rounded-2xl rounded-tl-none"}`}>
                {m.text}
                <div className={`text-[10px] mt-1 ${mine ? "text-amber-100/70" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}{mine && ` · ₹${m.cost ?? 1}`}</div>
              </div>
            </div>
          );
        })}
        {sending && other.is_ai && <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="chat-typing"><Loader2 className="w-3 h-3 animate-spin" /> {other.name.split(" ")[0]} is typing...</div>}
      </div>
      {blocked && (
        <div className="px-5 py-3 text-xs bg-amber-500/10 text-amber-200 border-t border-amber-500/20" data-testid="chat-blocked-banner">
          {data.needs_interest ? <>{other.name.split(" ")[0]} accepts messages only after accepting your interest. <Link to={`/profile/${other.id}`} className="underline">Send an interest</Link></>
            : !account?.plan_active ? <>You need an active plan to chat. <Link to="/plans" className="underline">See plans</Link></> : <>Your wallet is empty. <Link to="/wallet" className="underline">Top up</Link></>}
        </div>
      )}
      <form onSubmit={send} className="p-4 border-t border-slate-800 flex gap-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder={t("writeMsg")} className="field" data-testid="chat-input-field" disabled={sending} />
        <button type="submit" disabled={sending || !text.trim() || blocked} data-testid="chat-send-button" className="btn-gold px-5 rounded-xl flex items-center gap-2 shrink-0">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}<span className="hidden sm:inline" data-testid="chat-send-cost">₹{cost}</span>
        </button>
      </form>
    </section>
  );
};

export default function Chat() {
  const { pid } = useParams();
  const { authed } = useAccount();
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const loadInbox = useCallback(() => authed("get", "/conversations").then(setItems).catch(() => {}), [authed]);
  useEffect(() => { loadInbox(); const t = setInterval(loadInbox, 10000); return () => clearInterval(t); }, [loadInbox]);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8" data-testid="chat-page">
      {pid && <button onClick={() => navigate("/chat")} className="lg:hidden text-sm text-amber-300 mb-3" data-testid="chat-back-button">← All messages</button>}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-9rem)]">
        <Inbox items={items} active={pid} />
        {pid ? <Thread pid={pid} onSent={loadInbox} /> : (
          <div className="hidden lg:flex lg:col-span-8 card-royal rounded-2xl flex-col items-center justify-center text-center p-10">
            <MessageCircle className="w-12 h-12 text-amber-500/50" />
            <p className="font-display text-2xl mt-4">Choose a conversation</p>
            <p className="text-sm text-slate-400 mt-2">or <Link to="/browse" className="text-amber-300 underline">browse profiles</Link> to start one.</p>
          </div>
        )}
      </div>
    </div>
  );
}
